import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { SkillBiteEditSlice } from '../../../reducers'
import { Box } from '@mui/material'
import SessionSelector from './components/session-selector'
import PageSelector from './components/page-selector'
import PageBody from './components/page-body'
import EmptyIndicator from './components/empty-indicator'
import makeStyles from '@mui/styles/makeStyles'
import { appFooterContainerId } from '../../../layouts/primary-layout'
import { getBuildHeight } from './utils/get-build-height'
import PageBodyOverlay from './components/page-body-overlay'
import { useConfirm } from 'material-ui-confirm'
import { TabChangeGuardContext } from '..'

const useStyles = makeStyles(() => ({
  pageContainer: {
    overflowY: 'auto',
    maxHeight: getBuildHeight(),
    height: getBuildHeight(),
    margin: 0,
    padding: 0,
    /* Firefox */
    scrollbarWidth: 'thin',
    scrollbarColor: '#A6A6A6 #EBEBEB',
    scrollBehavior: 'smooth',
    scrollPaddingRight: '4px',
    paddingRight: '4px',
    listStyle: 'none',
    '&::-webkit-scrollbar': {
      width: '8px',
      marginRight: '2px',
      marginLeft: '2px'
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '5px',
      backgroundColor: '#EBEBEB'
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '5px',
      backgroundColor: '#A6A6A6'
    }
  }
}))

export const PageBodyOverlayContext = React.createContext(null)
export const BuildNavigationContext = React.createContext(null)

const Build = () => {
  const skillBite = useSelector(SkillBiteEditSlice.selectors.selectSkillBite)
  const { sessions } = skillBite
  const classes = useStyles()
  const pageBlocksFormRef = useRef()
  const pageBodyOverlayRef = useRef()
  const { tabChangeGuard } = useContext(TabChangeGuardContext)
  const pageContainerRef = useRef()
  const dispatch = useDispatch()
  const confirm = useConfirm()
  const canEdit = useSelector(SkillBiteEditSlice.selectors.selectEditMode)

  useEffect(() => {
    document.getElementById(appFooterContainerId).style.display = 'none'
    return () => {
      document.getElementById(appFooterContainerId).style.display = 'block'
    }
  }, [])

  const discardChangesOnNavigationGuard = useCallback((callback) => {
    const pageBlockForm = pageBlocksFormRef.current
    if (pageBlockForm.formState.isDirty && canEdit) {
      confirm({
        title: 'Discard changes?',
        description: 'You have unsaved changes on your page blocks form, are you sure you want to exit without saving?',
        confirmationText: 'Yes, discard',
        cancellationText: 'No, keep editing'
      })
        .then(callback)
        .catch(() => { })
      return
    }

    callback()
  }, [confirm, canEdit])

  useEffect(() => {
    if (tabChangeGuard) {
      tabChangeGuard.current = discardChangesOnNavigationGuard
    }
    return () => {
      if (tabChangeGuard) {
        tabChangeGuard.current = null
      }
    }
  }, [tabChangeGuard, discardChangesOnNavigationGuard])

  const resetPageContainerScroll = useCallback(() => {
    pageContainerRef.current.scrollTop = 0
  }, [])

  const changeSelectedSession = useCallback((session) => {
    discardChangesOnNavigationGuard(() => {
      dispatch(SkillBiteEditSlice.actions.changeSelectedSession(session))
      resetPageContainerScroll()
    })
  }, [dispatch, discardChangesOnNavigationGuard, resetPageContainerScroll])

  const changeSelectedSection = useCallback((section) => {
    discardChangesOnNavigationGuard(() => {
      dispatch(SkillBiteEditSlice.actions.changeSelectedSection(section))
      resetPageContainerScroll()
    })
  }, [dispatch, discardChangesOnNavigationGuard, resetPageContainerScroll])

  const changeSelectedPage = useCallback((page) => {
    discardChangesOnNavigationGuard(() => {
      dispatch(SkillBiteEditSlice.actions.changeSelectedPage(page))
      resetPageContainerScroll()
    })
  }, [dispatch, discardChangesOnNavigationGuard, resetPageContainerScroll])

  return (
    <BuildNavigationContext.Provider
      value={{
        changeSelectedSession,
        changeSelectedSection,
        changeSelectedPage,
        discardChangesOnNavigationGuard,
        pageBlocksFormRef
      }}
    >
      <Box display='flex' flexDirection='row' sx={{ padding: '16px' }}>
        <Box sx={{ width: '30%' }}>
          <SessionSelector sessions={sessions} />
        </Box>
        <Box display='flex' flexDirection='column' sx={{ flex: 1, p: 2, pr: 0, overflow: 'hidden' }}>
          <PageSelector />
          <PageBodyOverlayContext.Provider value={pageBodyOverlayRef}>
            <Box sx={{ position: 'relative' }}>
              <Box className={classes.pageContainer} ref={pageContainerRef} id='sb-page-container'>
                <PageBody />
                <EmptyIndicator />
              </Box>
              <PageBodyOverlay
                ref={pageBodyOverlayRef}
              />
            </Box>
          </PageBodyOverlayContext.Provider>
        </Box>
      </Box>
    </BuildNavigationContext.Provider>
  )
}

export default Build
